import { Text } from "@chakra-ui/react";
import React from "react";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import { EntityProfileEmptyState } from "../../../../entityProfileModal/components/EntityProfileFeedback";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileRecentActivityTimeline, {
  type EntityProfileRecentActivityTimelineItem,
} from "../../../../entityProfileModal/components/EntityProfileRecentActivityTimeline";
import { formatCaregiverProfileValue } from "../../../caregiverProfileModal.utils";
import { useCaregiverRecentActivity } from "./CaregiverProfileRecentActivitySection.hooks";
import type {
  CaregiverRecentActivityChange,
  CaregiverRecentActivityRow,
} from "./caregiverRecentActivity.types";

type Props = {
  caregiverId: CaregiverId;
};

function formatDateTime(value: unknown) {
  return formatCaregiverProfileValue(value) === "" ? "" : dateFormatter.toDateTime(value as never);
}

function formatTableName(value: string | null | undefined) {
  return (value ?? "").replace(/_/g, " ");
}

function getUserName(event: CaregiverRecentActivityRow) {
  switch (event.updatedByUserType) {
    case "CAREGIVER":
    case "AGENCY":
      return event.updatedByUser ?? String(event.updatedBy ?? "");
    case "MEDFLYT":
      return "Medflyt team";
    case "OTHER_AGENCY":
    default:
      return "The system";
  }
}

function getOperation(event: CaregiverRecentActivityRow) {
  const firstChange = event.changes[0];
  const hasOldValue = firstChange?.old !== null && firstChange?.old !== undefined;
  const firstLetter = firstChange?.title?.trim().charAt(0).toLowerCase() ?? "";
  const article = ["a", "e", "i", "o"].includes(firstLetter) ? "an" : "a";
  const subTableName = formatTableName(event.subTable);
  const tableName = formatTableName(event.tableName);
  const subTableDisplay = event.subTableDisplay ? ` (${event.subTableDisplay})` : "";

  switch (event.actionStr) {
    case "INSERT":
      return { entity: event.customName ?? tableName, name: "has created the" };
    case "UPDATE":
      return { entity: "", name: `has ${hasOldValue ? "updated" : "set"} the` };
    case "DELETE":
    case "SOFT_DELETE":
      return { entity: event.customName ?? tableName, name: "has deleted the" };
    case "SUB_INSERT":
      return { entity: `${subTableName}${subTableDisplay}`, name: `has added a` };
    case "SUB_UPDATE":
      return {
        entity: `${subTableName}${subTableDisplay}`,
        name: `has ${hasOldValue ? "updated" : "set"} ${article}`,
      };
    case "SUB_DELETE":
    case "SUB_SOFT_DELETE":
      return { entity: subTableName, name: "has deleted a" };
    case "ASSOCIATE":
      return {
        entity: `${subTableName}${subTableDisplay}`,
        name: `has associated this ${tableName} with ${article}`,
      };
    case "DISASSOCIATE":
      return {
        entity: `${subTableName}${subTableDisplay}`,
        name: `has disassociated this ${tableName} with ${article}`,
      };
    default:
      return { entity: "", name: event.actionStr };
  }
}

function formatChangeValue(change: CaregiverRecentActivityChange, key: "new" | "old") {
  const value = change[key];

  if (value === null || value === undefined || value === "") {
    return <Text as="i">None</Text>;
  }

  if (change.fieldType?.includes("timestamp") === true || change.fieldType === "date") {
    return formatDateTime(value);
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : <Text as="i">None</Text>;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    return "changed";
  }

  return formatCaregiverProfileValue(value);
}

function getChangePrefix(change: CaregiverRecentActivityChange, key: "new" | "old") {
  const hasOldValue = change.old !== null && change.old !== undefined && change.old !== "";
  const hasNewValue = change.new !== null && change.new !== undefined && change.new !== "";

  if (hasOldValue && hasNewValue) {
    return key === "old" ? " from " : " to ";
  }

  if (hasOldValue && !hasNewValue) {
    return key === "old" ? ": " : "";
  }

  return key === "new" ? ": " : "";
}

function HistoryChangeText(props: { change: CaregiverRecentActivityChange }) {
  const { change } = props;

  if (change.old === change.new) {
    return null;
  }

  return (
    <Text as="span">
      {" "}
      {change.title}
      {change.old !== undefined && change.old !== null ? (
        <>
          {getChangePrefix(change, "old")}
          <Text as="strong">{formatChangeValue(change, "old")}</Text>
        </>
      ) : null}
      {change.new !== undefined && change.new !== null ? (
        <>
          {getChangePrefix(change, "new")}
          <Text as="strong">{formatChangeValue(change, "new")}</Text>
        </>
      ) : null}
    </Text>
  );
}

function getCategories(activity: CaregiverRecentActivityRow) {
  return Array.from(
    new Set(activity.changes.map((change) => change.category || "General Details"))
  );
}

export default function CaregiverProfileRecentActivitySection(props: Props) {
  const activityQuery = useCaregiverRecentActivity(props.caregiverId);
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([]);
  const activities = React.useMemo(
    () =>
      (activityQuery.data?.list ?? [])
        .filter((activity) => activity.changes.some((change) => change.old !== change.new))
        .sort((a, b) => String(b.updatedAt ?? "").localeCompare(String(a.updatedAt ?? ""))),
    [activityQuery.data?.list]
  );
  const categoryOptions = React.useMemo(
    () => Array.from(new Set(activities.flatMap(getCategories))).sort((a, b) => a.localeCompare(b)),
    [activities]
  );
  const visibleActivities = React.useMemo(
    () =>
      selectedCategories.length === 0
        ? activities
        : activities.filter((activity) =>
            getCategories(activity).some((category) => selectedCategories.includes(category))
          ),
    [activities, selectedCategories]
  );
  const timelineItems = React.useMemo<EntityProfileRecentActivityTimelineItem[]>(
    () =>
      visibleActivities.map((activity) => {
        const operation = getOperation(activity);
        const userName = getUserName(activity);

        return {
          actorName: userName,
          actorPhotoUrl: activity.photoUrl ?? "admin/images/blank-profile.jpg",
          category: getCategories(activity)[0] ?? "General Details",
          content: (
            <>
              <Text as="span">{operation.name}</Text>{" "}
              {operation.entity ? (
                <Text as="span" fontWeight={700}>
                  {operation.entity}
                </Text>
              ) : null}
              {activity.changes
                .filter((change) => change.old !== change.new)
                .map((change) => (
                  <HistoryChangeText key={`${activity.id}-${change.title}`} change={change} />
                ))}
            </>
          ),
          date: formatDateTime(activity.updatedAt),
          id: activity.id,
        };
      }),
    [visibleActivities]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load recent activity."
      isError={activityQuery.isError}
      isLoading={activityQuery.isLoading}
      loadingLabel="Loading recent activity"
      onRetry={() => void activityQuery.refetch()}
    >
      {activities.length === 0 ? (
        <EntityProfileEmptyState>No recent activity.</EntityProfileEmptyState>
      ) : (
        <EntityProfileRecentActivityTimeline
          categories={categoryOptions}
          items={timelineItems}
          maxH="500px"
          selectedCategories={selectedCategories}
          onSelectedCategoriesChange={setSelectedCategories}
        />
      )}
    </EntityProfileQuerySection>
  );
}
